import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

/**
 * A single override toggle rendered as a cell inside {@link BypassChecksPanel}.
 * Carries no outer border of its own — the grouping border comes from the panel;
 * each row only gets a light amber cell outline so the 2-column grid reads clearly.
 */
export function BypassCheckRow({
	id,
	keyPrefix,
	checked,
	onCheckedChange,
	disabled,
}: {
	/** Unique DOM id for the switch and its description (e.g. "bypass-journey-check"). */
	id: string;
	/** i18n keyPrefix in the `appointments-form` namespace holding `label` + `description`. */
	keyPrefix: string;
	checked: boolean;
	onCheckedChange: (value: boolean) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation("appointments-form", { keyPrefix });
	const descriptionId = `${id}-description`;

	return (
		<div className="flex h-full flex-row items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-50/40 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
			<div className="min-w-0 space-y-0.5">
				<Label htmlFor={id} className="text-amber-900 dark:text-amber-200">
					{t("label")}
				</Label>
				<p
					id={descriptionId}
					className="text-pretty text-xs text-amber-800/80 dark:text-amber-300/80"
				>
					{t("description")}
				</p>
			</div>
			<Switch
				id={id}
				checked={checked}
				onCheckedChange={onCheckedChange}
				disabled={disabled}
				aria-describedby={descriptionId}
				className="!mt-0"
			/>
		</div>
	);
}

/**
 * Admin-gated grouped container for the appointment "bypass" overrides (allow
 * slot overlap, schedule outside the contract window, ignore travel limits).
 * Collapses what would otherwise be several separate amber warning boxes into a
 * single warning zone: one header + caption, then the toggles in a 2-column grid
 * on large screens (single column on mobile). Renders nothing for non-ADMIN
 * users; the gate is UX-only — the server remains the authority.
 *
 * Pass one {@link BypassCheckRow} per override as children (optionally wrapped in
 * a react-hook-form `FormField`).
 */
export function BypassChecksPanel({
	children,
	cols = 2,
}: {
	children: ReactNode;
	/**
	 * Toggle grid columns on large screens. Use 2 for the wide reschedule form
	 * (saves vertical space) and 1 inside the narrow bulk confirmation drawer.
	 */
	cols?: 1 | 2;
}) {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "override_checks",
	});

	return (
		<div className="col-span-full rounded-md border border-amber-500/50 bg-amber-50/50 p-3 shadow-inner dark:border-amber-900/50 dark:bg-amber-950/20">
			<div className="mb-3 flex items-start gap-2">
				<AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500" />
				<div className="min-w-0 flex-1">
					<p className="text-xs font-semibold uppercase tracking-tight text-amber-900 dark:text-amber-200">
						{t("title")}
					</p>
					<p className="text-pretty text-xs text-amber-800/80 dark:text-amber-300/80">
						{t("description")}
					</p>
				</div>
				<span className="shrink-0 rounded-full border border-amber-500/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-900/50 dark:text-amber-300">
					{t("admin_badge")}
				</span>
			</div>
			<div
				className={
					cols === 2
						? "grid grid-cols-1 gap-2 md:grid-cols-2"
						: "grid grid-cols-1 gap-2"
				}
			>
				{children}
			</div>
		</div>
	);
}
